import { useMemo } from "react";

import { type AddressValue } from "@/lib/address";
import { cn } from "@/lib/utils";
import {
  type SupportedCountry,
  statesForCountry,
  validatePostalCode,
  validateState,
} from "@/lib/validation";

/**
 * Shared structured-address editor. Replaces the free-text address
 * textareas that previously lived inline on every page (org detail,
 * org create, location detail, location create, onboard wizard).
 *
 * The AddressValue type + pure helpers live in @/lib/address so
 * Vite's react-refresh is happy (components-only file). Validation
 * rules live in @/lib/validation.
 */

export interface AddressEditorProps {
  value: AddressValue;
  onChange: (next: AddressValue) => void;
  /** Namespace for input `id`s so two editors on one page don't collide. */
  idPrefix: string;
  /** Disables all inputs (read-only mode). */
  disabled?: boolean;
  /** Gate validation messages on submit vs live. Default: live. */
  showErrors?: boolean;
  /** All fields required by default. Pass false to make the whole
   *  address optional (e.g. contact address on an org). */
  required?: boolean;
}

export function AddressEditor(props: AddressEditorProps): JSX.Element {
  const { value, onChange, idPrefix, disabled, showErrors = true, required = true } = props;
  const states = useMemo(() => statesForCountry(value.country), [value.country]);
  const anyFilled =
    value.line1.trim() || value.city.trim() || value.state || value.postal_code.trim();
  const effectiveRequired = required || !!anyFilled;
  const stateCheck = validateState(value.state, value.country, { required: effectiveRequired });
  const postalCheck = validatePostalCode(value.postal_code, value.country, {
    required: effectiveRequired,
  });

  const set = <K extends keyof AddressValue>(key: K, v: AddressValue[K]): void =>
    onChange({ ...value, [key]: v });

  return (
    <div className="space-y-2">
      <div>
        <label
          htmlFor={`${idPrefix}-line1`}
          className="mb-1 block text-xs font-medium text-muted-foreground"
        >
          Address line 1{effectiveRequired ? " *" : ""}
        </label>
        <input
          id={`${idPrefix}-line1`}
          type="text"
          value={value.line1}
          onChange={(e) => set("line1", e.target.value)}
          disabled={disabled}
          required={effectiveRequired}
          autoComplete="address-line1"
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          placeholder="123 Main St"
        />
      </div>
      <div>
        <label
          htmlFor={`${idPrefix}-line2`}
          className="mb-1 block text-xs font-medium text-muted-foreground"
        >
          Address line 2 <span className="text-muted-foreground/80">(optional)</span>
        </label>
        <input
          id={`${idPrefix}-line2`}
          type="text"
          value={value.line2}
          onChange={(e) => set("line2", e.target.value)}
          disabled={disabled}
          autoComplete="address-line2"
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          placeholder="Suite 400"
        />
      </div>
      <div className="grid gap-2 sm:grid-cols-[2fr_1fr_1fr]">
        <div>
          <label
            htmlFor={`${idPrefix}-city`}
            className="mb-1 block text-xs font-medium text-muted-foreground"
          >
            City{effectiveRequired ? " *" : ""}
          </label>
          <input
            id={`${idPrefix}-city`}
            type="text"
            value={value.city}
            onChange={(e) => set("city", e.target.value)}
            disabled={disabled}
            required={effectiveRequired}
            autoComplete="address-level2"
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label
            htmlFor={`${idPrefix}-state`}
            className="mb-1 block text-xs font-medium text-muted-foreground"
          >
            {value.country === "US" ? "State" : "Province"}
            {effectiveRequired ? " *" : ""}
          </label>
          <select
            id={`${idPrefix}-state`}
            value={value.state}
            onChange={(e) => set("state", e.target.value)}
            disabled={disabled}
            required={effectiveRequired}
            aria-invalid={showErrors && !stateCheck.ok}
            autoComplete="address-level1"
            className={cn(
              "w-full rounded-md border bg-background px-2 py-2 text-sm",
              showErrors && !stateCheck.ok && "border-destructive",
            )}
          >
            <option value=""></option>
            {states.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            htmlFor={`${idPrefix}-postal`}
            className="mb-1 block text-xs font-medium text-muted-foreground"
          >
            {value.country === "US" ? "ZIP" : "Postal"}
            {effectiveRequired ? " *" : ""}
          </label>
          <input
            id={`${idPrefix}-postal`}
            type="text"
            value={value.postal_code}
            onChange={(e) => set("postal_code", e.target.value)}
            disabled={disabled}
            required={effectiveRequired}
            aria-invalid={showErrors && !postalCheck.ok}
            autoComplete="postal-code"
            className={cn(
              "w-full rounded-md border bg-background px-3 py-2 font-mono text-sm",
              showErrors && !postalCheck.ok && "border-destructive",
            )}
            placeholder={value.country === "US" ? "80202" : "M5H 2N2"}
          />
        </div>
      </div>
      <div>
        <label
          htmlFor={`${idPrefix}-country`}
          className="mb-1 block text-xs font-medium text-muted-foreground"
        >
          Country{effectiveRequired ? " *" : ""}
        </label>
        <select
          id={`${idPrefix}-country`}
          value={value.country}
          onChange={(e) => set("country", e.target.value as SupportedCountry)}
          disabled={disabled}
          autoComplete="country"
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
        >
          <option value="US">United States</option>
          <option value="CA">Canada</option>
        </select>
      </div>
      {showErrors && !stateCheck.ok && (
        <p className="text-[11px] text-destructive">{stateCheck.error}</p>
      )}
      {showErrors && !postalCheck.ok && (
        <p className="text-[11px] text-destructive">{postalCheck.error}</p>
      )}
    </div>
  );
}
